import { FormType, ReportJSON } from '@aloft/toolkit-shared';

import { scalarToNumberToString } from '../utils.ts';
import { createTextFieldNode } from './createTextFieldNode.ts';
import { createUnitIndexNode } from './createUnitIndexNode.ts';

export const generateTkv2AdjustmentsRptiNodes = (
  doc: Document,
  { salesGridData: { adjustments } }: ReportJSON['comps'][string],
  compIndex: number,
  formType: FormType,
  meta?: ReportJSON['meta'],
): HTMLElement[] => {
  const rptiNodes = [];
  const additionalAttributes = {
    BaseCompIndex: (compIndex + 1).toString(),
    CompType: 'SalesComparables',
  };

  // Sales Concessions
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'SaleConcessionsAdjustment',
      Data: scalarToNumberToString(adjustments?.SalesConcessions),
      additional: additionalAttributes,
    }),
  );

  // Financing Concessions
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'FinancingConcessionsAdjustment',
      Data: scalarToNumberToString(adjustments?.FinancingConcessions),
      additional: additionalAttributes,
    }),
  );

  // Date of Sale
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'SaleDateAdjustment',
      Data: scalarToNumberToString(adjustments?.DateOfSale),
      additional: additionalAttributes,
    }),
  );

  // Site/ Location and View
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'SiteAdjustment',
      Data: scalarToNumberToString(adjustments?.SiteArea),
      additional: additionalAttributes,
    }),
  );

  // Construction quality
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'ConstructionQualityAdjustment',
      Data: scalarToNumberToString(adjustments?.Quality),
      additional: additionalAttributes,
    }),
  );

  // Actual Age
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'ActualAgeAdjustment',
      additional: additionalAttributes,
    }),
  );

  // Bathroom adjustment
  const bathroomAdjustmentID =
    formType !== FormType.fnma1025 ? 'RoomCountAdjustment' : 'RoomAdjustment';
  rptiNodes.push(
    createTextFieldNode(
      doc,
      {
        Id: bathroomAdjustmentID,
        Data: scalarToNumberToString(adjustments?.RoomAboveGradeLine2),
        additional: additionalAttributes,
      },
      createUnitIndexNode(doc, 1),
    ),
  );

  const grossAreaNode =
    formType === FormType.fnma1025 ? 'GrossBuildingAreaAdjustment' : 'GrossLivingAreaAdjustment';

  // Gross Living Area (GLA)/GBA
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: grossAreaNode,
      Data: scalarToNumberToString(adjustments?.GrossLivingArea),
      additional: additionalAttributes,
    }),
  );

  // Number of Units adjustment

  rptiNodes.push(
    createTextFieldNode(
      doc,
      {
        Id: 'RoomAdjustment',
        Data: scalarToNumberToString(adjustments?.NumberOfUnits),
        // Second line gets the number of units adj
        additional: additionalAttributes,
      },
      createUnitIndexNode(doc, 2),
    ),
  );

  // Condition
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'ConditionAdjustment',
      Data: scalarToNumberToString(adjustments?.Condition),
      additional: additionalAttributes,
    }),
  );

  // Basement
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'BasementAdjustment',
      Data: scalarToNumberToString(adjustments?.BasementArea),
      additional: additionalAttributes,
    }),
  );

  // Basement Finish (Below Grade Baths)
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'RoomsBelowGradeAdjustment',
      Data: scalarToNumberToString(adjustments?.BasementFinish),
      additional: additionalAttributes,
    }),
  );

  // Functional Utility
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'FunctionalUtilityAdjustment',
      Data: scalarToNumberToString(adjustments?.FunctionalUtility),
      additional: additionalAttributes,
    }),
  );

  // Car Storage
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'CarStorageAdjustment',
      Data: scalarToNumberToString(adjustments?.CarStorage),
      additional: additionalAttributes,
    }),
  );

  // Porch Patio Deck
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'PorchPatioDeckAdjustment',
      Data: scalarToNumberToString(adjustments?.PorchDeck),
      additional: additionalAttributes,
    }),
  );

  // Additional Structures adjustment
  rptiNodes.push(
    createTextFieldNode(doc, {
      Id: 'BlankAdjustment',
      Data: scalarToNumberToString(adjustments?.OtherFeature1),
      additional: additionalAttributes,
    }),
  );

  if (meta?.otherFeature2Label === 'External Obsolescence') {
    // External obsolescence
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'Blank2Adjustment',
        Data: scalarToNumberToString(adjustments?.OtherFeature2),
        additional: additionalAttributes,
      }),
    );
  }

  return rptiNodes;
};
